import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';

import { getBrandmanagerURL } from './config';

import Observer from '@xingternal/intersection-observer';
import { ContentSwitcher } from '@xingternal/content-switcher';
import { trackContentSwitcherFirstUse } from '@xingternal/pages-tracking';

import {
  useEditContext,
  usePageContext,
  ExternalEditInfoBanner,
} from '@xingternal/pages-common';

import styles from './Navigation.css';

type NavigationProps = {
  pageModulesByType: Array<string>;
  navigationRef: (node: any) => void;
  navigationOffset: number;
  frameSize: {
    width: number;
    height: number;
  };
  activeIndex: number;
  forceActiveIndex: (activeIndex: number) => void;
  navigationBarHeight: number;
};

const Navigation = ({
  pageModulesByType,
  navigationRef,
  navigationOffset,
  frameSize,
  activeIndex,
  forceActiveIndex,
  navigationBarHeight,
}: NavigationProps) => {
  const intl = useIntl();
  const { isEditing, isEditor } = useEditContext();
  const [isSticky, setSticky] = useState(false);
  const [firstNavClick, setFirstNavClick] = useState(true);

  const { pageContext } = usePageContext();

  const scrollToModule = (type: string) => {
    const moduleOffset =
      window.pageYOffset +
      document.getElementById(`module-${type}`)!.getBoundingClientRect().top;
    const margin = 16;

    forceActiveIndex(
      pageModulesByType.findIndex((pageModuleType) => pageModuleType === type)
    );

    if (firstNavClick) {
      trackContentSwitcherFirstUse(type);
      setFirstNavClick(false);
    }

    window.scrollTo({
      top: moduleOffset - navigationOffset - margin,
      behavior: 'smooth',
    });
  };

  const data = pageModulesByType.map((pageModuleType) => ({
    copy: intl.formatMessage({
      id: 'EP_' + pageModuleType.toUpperCase() + '_NAVIGATION_TITLE',
    }),
    onClick: () => module && scrollToModule(pageModuleType),
  }));

  return (
    <div
      className={cx(styles.navigationMainWrapper, {
        [styles.navigationWrapper]: !isEditing,
      })}
    >
      <Observer
        onChange={({
          rootBounds,
          boundingClientRect,
        }: IntersectionObserverEntry) =>
          rootBounds && setSticky(boundingClientRect.y < rootBounds.y)
        }
        rootMargin={`-${frameSize.height}px 0px 0px 0px`}
        threshold={[1]}
      >
        <div className={cx(styles.navigationSentinal)} />
      </Observer>
      <div
        style={{
          height: navigationBarHeight > 0 ? navigationBarHeight : 'inherit',
        }}
      >
        <div
          className={cx(styles.navigation, {
            [styles.navigationSticky]: isSticky,
            [styles.isEditing]: isEditing,
            [styles.isEditor]: isEditor,
            [styles.notDraft]: pageContext?.publicationStatus !== 'DRAFT',
          })}
          style={{
            height: navigationBarHeight > 0 ? navigationBarHeight : 'inherit',
            width: frameSize.width > 0 ? frameSize.width + 40 : 'inherit',
          }}
        >
          <div className={styles.container} ref={navigationRef}>
            <div className={styles.contentSwitcherContainer}>
              <ContentSwitcher
                activeItemIndex={activeIndex}
                variant="filled"
                size="small"
                isFixed={isSticky}
                data={data}
                className={styles.contentSwitcher}
              />
            </div>
          </div>
          <div className={styles.navigationBorder} />
        </div>
      </div>

      {isEditing && pageContext?.publicationStatus !== 'DRAFT' && (
        <div className={styles.infoBanner}>
          <ExternalEditInfoBanner
            copyKey={'EP_NAVIGATION_EDIT_HINT'}
            ctaCopyKey={'EP_NAVIGATION_EDIT_HINT_CTA'}
            ctaUrl={getBrandmanagerURL(pageContext?.pageSlug)}
            cyTestId={'EDIT_HINT_NAVIGATION'}
            ctaAvailableInMobile={false}
          />
        </div>
      )}
    </div>
  );
};

export default Navigation;
